import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './admin/UserModal.css'; // 기존 모달 스타일 재사용

/**
 * 비밀번호 입력 모달 컴포넌트
 * 개발자 모드 토글, 관리자 인증 등에서 재사용 가능
 */
const PasswordPromptModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  title = "비밀번호 입력",
  message = "비밀번호를 입력하세요",
  placeholder = "비밀번호",
  confirmText = "확인",
  cancelText = "취소"
}) => {
  const [password, setPassword] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // 비밀번호 입력 필드 참조
  const passwordInputRef = useRef(null);

  // ESC 키 이벤트 핸들러
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen && !isProcessing) {
        event.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown, { passive: false }); // preventDefault 사용으로 passive: false
      // 모달이 열릴 때 body 스크롤 방지
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // 모달이 닫힐 때 body 스크롤 복원
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isProcessing]);

  // 모달이 열릴 때 비밀번호 필드에 포커스
  useEffect(() => {
    if (isOpen) {
      setPassword(''); // 모달이 열릴 때마다 비밀번호 초기화
      setErrorMessage(''); // 에러 메시지도 초기화
      const timer = setTimeout(() => {
        if (passwordInputRef.current) {
          passwordInputRef.current.focus();
        }
      }, 100); // 모달 애니메이션 후 포커스

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!isProcessing) {
      setPassword('');
      setErrorMessage('');
      onClose();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isProcessing) {
      handleClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password.trim() || isProcessing) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(''); // 제출 시 기존 에러 메시지 클리어
    
    try {
      await onConfirm(password);
      // 성공하면 모달 닫기 (onConfirm에서 처리하지 않고 여기서 처리)
      setPassword('');
      setErrorMessage('');
    } catch (error) {
      // 에러 메시지를 모달 내에서 표시
      setErrorMessage(error.message || '비밀번호가 올바르지 않습니다.');
      setPassword(''); // 비밀번호 입력창 클리어하여 재입력 유도
      // 에러 발생 시 비밀번호 필드에 다시 포커스
      setTimeout(() => {
        if (passwordInputRef.current) {
          passwordInputRef.current.focus();
        }
      }, 100);
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    // 사용자가 입력을 시작하면 에러 메시지 클리어
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  // 모달이 닫혀있을 때는 아무것도 렌더링하지 않음
  if (!isOpen) return null;

  // Portal을 사용하여 document.body에 직접 렌더링
  const modalContent = (
    <div 
      className="user-modal-overlay" 
      style={{
        zIndex: 1000000, // 높은 z-index 설정
      }}
      onClick={handleOverlayClick}
    >
      <div className="user-modal" style={{ 
        maxWidth: '400px', 
        width: '90vw'
      }}>
        <div className="user-modal-header">
          <h3>
            <span style={{ marginRight: '8px' }}>🔐</span>
            {title}
          </h3>
          <button 
            onClick={handleClose} 
            className="user-modal-close"
            disabled={isProcessing}
          >
            ✕
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="user-modal-form" noValidate>
          <div className="user-modal-body">
            {message && (
              <div style={{
                marginBottom: '20px',
                color: '#4a5568',
                fontSize: '14px',
                lineHeight: '1.5'
              }}>
                {message}
              </div>
            )}
            
            {/* 에러 메시지 표시 */}
            {errorMessage && (
              <div style={{
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#dc2626',
                fontSize: '14px',
                lineHeight: '1.5',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '16px' }}>⚠️</span>
                {errorMessage}
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="password" style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                비밀번호 *
              </label>
                              <input
                  ref={passwordInputRef}
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder={placeholder}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: `1px solid ${errorMessage ? '#f87171' : '#d1d5db'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    backgroundColor: errorMessage ? '#fef2f2' : 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = errorMessage ? '#f87171' : '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = errorMessage ? '#f87171' : '#d1d5db'}
                  disabled={isProcessing}
                  autoComplete="off"
                />
            </div>
          </div>

          <div className="user-modal-footer">
            <button 
              type="button"
              onClick={handleClose}
              className="btn-cancel"
              disabled={isProcessing}
            >
              {cancelText}
            </button>
            <button 
              type="submit"
              className="btn-save"
              disabled={isProcessing || !password.trim()}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {isProcessing ? (
                <>
                  <span style={{ 
                    display: 'inline-block',
                    width: '14px',
                    height: '14px',
                    border: '2px solid #ffffff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}>
                  </span>
                  처리 중...
                </>
              ) : (
                confirmText
              )}
            </button>
          </div>
        </form>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );

  // Portal을 사용하여 document.body에 렌더링
  return createPortal(modalContent, document.body);
};

export default PasswordPromptModal; 